import type { TaskId } from '../domain/branded.js';
import type { Project } from '../domain/project.js';
import type { Task } from '../domain/task.js';

/**
 * WBS（工作分解結構）碼推導。
 *
 * **設計原則：WBS 一律由結構推導，絕不儲存為獨立事實。**
 * `Task.wbs` 只是推導結果的快取；任何會改變清單順序或 `outlineLevel` 的操作
 * （新增、刪除、搬移、縮排、凸排、貼上）之後都必須重新呼叫 `withWbsCodes()`，
 * 否則欄位會停在舊值 —— 那比沒有這個欄位更糟：看起來可信，說的卻是舊事實。
 *
 * 推導只依賴兩件事：**清單順序** 與 **`outlineLevel`**。不看 `parentId`、不看
 * `isSummary`，因為在結構編輯的中間狀態下那些欄位可能尚未收斂。
 */

/** 非法或缺失的 `outlineLevel` 一律夾制到 1。 */
const normaliseLevel = (level: number): number => {
  if (!Number.isFinite(level)) return 1;
  const truncated = Math.trunc(level);
  return truncated < 1 ? 1 : truncated;
};

/**
 * 依 `outlineLevel` 與清單順序推導階層式 WBS 碼。
 *
 * 走訪一次清單，維護一個以層級為索引的計數器堆疊：
 * - level 1 → `1`、`2`、`3`…
 * - level 2（在 `1` 之下）→ `1.1`、`1.2`…
 * - level 3（在 `1.2` 之下）→ `1.2.1`…
 *
 * **回到較淺的層級時，比它更深的計數器一律歸零。** 例如 `1.1.1` 之後回到 level 2
 * 會得到 `1.2`，其下一個 level 3 必須重新從 `1.2.1` 起算，而不是接續成 `1.2.2`。
 *
 * 邊界情況（皆不拋錯，行為明確且可預期）：
 * - **空清單** → 空 Map。
 * - **第一個任務的 `outlineLevel > 1`（孤兒深層任務）**：視為它所在層級、以及每一個
 *   被隱含跳過的祖先層級的第一個項目。例如清單第一項為 level 3 → `1.1.1`。
 * - **層級跳級（1 → 3，中間沒有 2）**：同上，被跳過的中間層級一律補 `1`。
 *   例如 `1` 之後緊接一個 level 3 → `1.1.1`。
 * - **`outlineLevel` 為 0、負數、小數或非有限值** → 夾制／截斷到至少 1。
 *
 * 複雜度為單次線性走訪 O(n × 平均深度)（深度即輸出字串長度），無巢狀掃描。
 *
 * @param tasks 依顯示（大綱）順序排列的任務清單。
 * @returns TaskId → WBS 碼。重複的 id 以最後一次出現為準。
 */
export function computeWbsCodes(tasks: readonly Task[]): ReadonlyMap<TaskId, string> {
  const codes = new Map<TaskId, string>();
  /** counters[i] = 第 (i + 1) 層目前的序號。陣列長度即目前所在深度。 */
  const counters: number[] = [];

  for (const task of tasks) {
    const level = normaliseLevel(task.outlineLevel);

    if (counters.length >= level) {
      // 同層或回到較淺層：截斷掉更深的計數器（歸零），再把本層序號加一。
      counters.length = level;
      counters[level - 1] = (counters[level - 1] ?? 0) + 1;
    } else {
      // 往更深層：補齊中間被跳過的層級，每一層都從 1 起算。
      while (counters.length < level) counters.push(1);
    }

    codes.set(task.id, counters.join('.'));
  }

  return codes;
}

/**
 * 回傳 `task.wbs` 已重算的新 Project。
 *
 * 不變更輸入：一律回傳新的 Project 物件與新的 `tasks` 陣列，但 **WBS 未改變的
 * Task 物件會沿用原參照**，讓呈現層的 memo 比較維持有效。
 *
 * 冪等：對同一個 project 連續呼叫兩次會得到相同結果。
 */
export function withWbsCodes(project: Project): Project {
  const codes = computeWbsCodes(project.tasks);
  const tasks = project.tasks.map((task) => {
    const wbs = codes.get(task.id);
    if (wbs === undefined || wbs === task.wbs) return task;
    return { ...task, wbs };
  });
  return { ...project, tasks };
}
